const ALL_TYPES = ["base", "parallel", "insert", "auto"];

export function readCardListFilters(root){
    const pressed = (key) => {
        const chip = root.querySelector(`[data-filter-key="${key}"][aria-pressed="true"]`);
        return chip ? chip.dataset.filterValue : "all";
    };
    const hidden = (key) => {
        const input = root.querySelector(`[data-hide-filter="${key}"]`);
        return input ? input.checked : false;
    };
    const searchInput = root.querySelector('[data-card-search]');
    const yearSelect = root.querySelector('#year-select');

    return {
        query: searchInput ? searchInput.value.trim().toLowerCase() : "",
        owned: pressed("owned"),
        wanted: hidden("wanted") ? "not" : pressed("wanted"),
        trade: hidden("trade") ? "not" : pressed("trade"),
        types: [...root.querySelectorAll('[data-type-filter]')].filter(input => input.checked).map(input => input.value),
        year: yearSelect ? yearSelect.value : "all"
    };
}

function typeAllowed(type, types){
    // Groups with no type, or one outside the known four, are never hidden by the type filter
    return !ALL_TYPES.includes(type) || types.includes(type);
}

export function cardMatches(card, filters){
    if (filters.query && !card.search.includes(filters.query)){
        return false;
    }
    if ((filters.owned === "missing" && card.owned > 0) || (filters.owned === "owned" && card.owned === 0)){
        return false;
    }
    if ((filters.wanted === "yes" && card.wanted === 0) || (filters.wanted === "not" && card.wanted > 0)){
        return false;
    }
    if ((filters.trade === "yes" && card.trade === 0) || (filters.trade === "not" && card.trade > 0)){
        return false;
    }
    return card.type === undefined || typeAllowed(card.type, filters.types);
}

export function groupMatches(group, filters){
    if (group.type !== undefined && !typeAllowed(group.type, filters.types)){
        return false;
    }
    return filters.year === "all" || group.year === filters.year;
}

export function countHiddenFilters(filters){
    return ALL_TYPES.filter(type => !filters.types.includes(type)).length +
        (filters.year !== "all" ? 1 : 0) +
        (filters.wanted === "not" ? 1 : 0) +
        (filters.trade === "not" ? 1 : 0);
}

export function hasAnyFilter(filters){
    return filters.query !== "" || filters.owned !== "all" || filters.wanted !== "all" ||
        filters.trade !== "all" || countHiddenFilters(filters) > 0;
}

export function describeFilters(filters){
    const labels = {
        owned: { missing: "Missing", owned: "Owned" },
        wanted: { yes: "Wanted", not: "Hiding wanted" },
        trade: { yes: "For trade", not: "Hiding for trade" }
    };
    const parts = [];
    if (filters.query){
        parts.push(`“${filters.query}”`);
    }
    ["owned", "wanted", "trade"].forEach(key => {
        if (labels[key][filters[key]]){
            parts.push(labels[key][filters[key]]);
        }
    });
    if (filters.types.length < ALL_TYPES.length){
        parts.push(filters.types.length ? filters.types.map(t => t[0].toUpperCase() + t.slice(1)).join("/") : "No card types");
    }
    if (filters.year !== "all"){
        parts.push(filters.year);
    }
    return parts.join(" · ");
}

export function readCardData(row){
    return {
        search: row.dataset.search || "",
        owned: Number(row.dataset.bsOwned) || 0,
        wanted: Number(row.dataset.bsWanted) || 0,
        trade: Number(row.dataset.bsTrade) || 0,
        type: row.dataset.bsCgType
    };
}

export function applyCardListFilters(root){
    const filters = readCardListFilters(root);
    let shown = 0;
    let total = 0;
    let shownGroups = 0;

    root.querySelectorAll('.card-group-item').forEach(group => {
        const groupVisible = groupMatches({ type: group.dataset.bsCardGroupType, year: group.dataset.bsSetYear }, filters);
        let groupShown = 0;
        group.querySelectorAll('.card-rows > .card-row').forEach(row => {
            total += 1;
            const visible = groupVisible && cardMatches(readCardData(row), filters);
            row.hidden = !visible;
            if (visible){
                groupShown += 1;
            }
        });
        group.hidden = groupShown === 0;
        shown += groupShown;
        if (groupShown > 0){
            shownGroups += 1;
        }
    });

    const anyFilter = hasAnyFilter(filters);
    const hiddenCount = countHiddenFilters(filters);
    const resultCount = root.querySelector('[data-result-count]');
    if (resultCount){
        resultCount.textContent = anyFilter ? `${shown} of ${total}` : `${total} cards`;
    }
    root.querySelectorAll('[data-clear-filters]').forEach(button => {
        if (!button.closest('[data-card-list-empty]')){
            button.hidden = !anyFilter;
        }
    });
    const empty = root.querySelector('[data-card-list-empty]');
    if (empty){
        empty.hidden = shownGroups > 0;
    }
    const moreButton = root.querySelector('[data-more-filters-btn]');
    const moreCount = root.querySelector('[data-more-filters-count]');
    if (moreButton && moreCount){
        moreButton.classList.toggle('is-active', hiddenCount > 0);
        moreCount.hidden = hiddenCount === 0;
        moreCount.textContent = hiddenCount;
        moreButton.setAttribute('aria-label', hiddenCount > 0 ? `Filters, ${hiddenCount} on` : 'Filters');
    }
    return { filters, shown, total };
}

export function resetCardListFilters(root){
    const searchInput = root.querySelector('[data-card-search]');
    if (searchInput){
        searchInput.value = "";
    }
    root.querySelectorAll('[data-filter-key]').forEach(chip => chip.setAttribute('aria-pressed', 'false'));
    root.querySelectorAll('[data-hide-filter]').forEach(input => { input.checked = false; });
    root.querySelectorAll('[data-type-filter]').forEach(input => { input.checked = true; });
    const yearSelect = root.querySelector('#year-select');
    if (yearSelect){
        yearSelect.value = "all";
    }
    return applyCardListFilters(root);
}

export function toggleFilterChip(root, chip){
    const turnOn = chip.getAttribute('aria-pressed') !== 'true';
    root.querySelectorAll(`[data-filter-key="${chip.dataset.filterKey}"]`).forEach(other => other.setAttribute('aria-pressed', 'false'));
    chip.setAttribute('aria-pressed', turnOn ? 'true' : 'false');
    // "Wanted" and "hide wanted" (likewise trade) contradict each other, so turning one on clears the other
    if (turnOn){
        const hideInput = root.querySelector(`[data-hide-filter="${chip.dataset.filterKey}"]`);
        if (hideInput){
            hideInput.checked = false;
        }
    }
}

export function clearChipForHideFilter(root, hideInput){
    if (hideInput.checked){
        root.querySelectorAll(`[data-filter-key="${hideInput.dataset.hideFilter}"]`).forEach(chip => chip.setAttribute('aria-pressed', 'false'));
    }
}
